import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, Typography, Button, Descriptions, Row, Col, Space, Spin, Tag, message, Tabs } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined, HeartOutlined } from '@ant-design/icons';
import { getNewsById, updateNews, likeNews } from '@/api/news';
import { News } from '@/types/community/news';
import { useAuth } from '@/contexts/AuthContext';
import NewsModal from '@/components/features/community/NewsModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import CommentsFormSection from '@/components/common/CommentsFormSection';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteNews } from '@/api/news';
import dayjs from 'dayjs';
import { 
  AlertTab, 
  EventTab, 
  GuidelineTab, 
  IncidentTab, 
  LostAndFoundTab 
} from '@/components/tab';

const { Title, Text, Paragraph } = Typography;

const newsCategoryColors: Record<News['category'], string> = {
  general: 'blue',
  alert: 'red',
  event: 'green',
  guideline: 'purple',
  incident: 'orange',
  lostAndFound: 'cyan',
  amenities: 'geekblue',
  policies: 'magenta',
  safety: 'volcano',
  security: 'purple'
};

const newsStatusColors: Record<News['status'], string> = {
  draft: 'orange',
  published: 'green',
  archived: 'default'
};

const NewsDetailsPage: React.FC = () => {
  const { t } = useTranslation(['news', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { news: News } | null;
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();

  // State for modals
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<Error | undefined>(undefined);

  // Queries - Always fetch to ensure we have the latest data
  const { data: news, isLoading, refetch } = useQuery({
    queryKey: ['news', id],
    queryFn: () => getNewsById(id!, firestoreUser!),
    enabled: !!id && !!firestoreUser,
    staleTime: 0, // Always consider data stale to allow immediate refetch
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Use state data if available and more recent, otherwise use fetched data
  const displayNews = state?.news && !news ? state.news : news;



  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (newsId: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteNews(newsId, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('news:newsDeleted'));
      queryClient.invalidateQueries({ queryKey: ['news'] });
      setIsDeleteModalVisible(false);
      setDeleteError(undefined);
      navigate('/community/news');
    },
    onError: (error: Error) => {
      setDeleteError(error);
    }
  });

  // Like mutation
  const likeMutation = useMutation({
    mutationFn: (newsId: string) => likeNews(newsId, firestoreUser!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
      refetch(); // Refresh the current news data
    },
    onError: (error: any) => {
      console.error('Error liking news:', error);
      message.error('Failed to like news');
    }
  });

  // Handlers
  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = () => {
    setIsDeleteModalVisible(true);
    setDeleteError(undefined);
  };

  const handleLike = () => {
    if (displayNews) {
      likeMutation.mutate(displayNews.id);
    }
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['news', id] });
  };

  const handleEditCancel = () => {
    setIsEditModalOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (id) {
      deleteMutation.mutate(id);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setDeleteError(undefined);
  };

  const handleErrorDismiss = () => {
    setDeleteError(undefined);
  };

  // Comment handlers
  const handleAddComment = async (comment: any) => {
    if (!displayNews || !firestoreUser) return;

    try {
      const newComment = {
        ...comment,
        createdAt: new Date()
      };

      const updatedComments = [...(displayNews.comments || []), newComment];

      await updateNews(displayNews.id, { comments: updatedComments }, firestoreUser);
      
      message.success(t('common:createSuccess'));
      
      // Always invalidate and refetch to get the latest data
      queryClient.invalidateQueries({ queryKey: ['news', id] });
      queryClient.invalidateQueries({ queryKey: ['news'] });
      await refetch();
    } catch (error: any) {
      console.error('Error adding comment:', error);
      message.error(error.message || t('common:createError'));
    }
  };

  const handleUpdateComment = async (commentIndex: number, content: string) => {
    if (!displayNews || !firestoreUser) return;

    try {
      const updatedComments = [...(displayNews.comments || [])];
      updatedComments[commentIndex] = {
        ...updatedComments[commentIndex],
        content
      };

      await updateNews(displayNews.id, { comments: updatedComments }, firestoreUser);
      
      message.success(t('common:updateSuccess'));
      
      // Always invalidate and refetch to get the latest data
      queryClient.invalidateQueries({ queryKey: ['news', id] });
      queryClient.invalidateQueries({ queryKey: ['news'] });
      await refetch();
    } catch (error: any) {
      console.error('Error updating comment:', error);
      message.error(error.message || t('common:updateError'));
    }
  };

  const handleDeleteComment = async (commentIndex: number) => {
    if (!displayNews || !firestoreUser) return;

    try {
      const updatedComments = [...(displayNews.comments || [])];
      updatedComments.splice(commentIndex, 1);

      await updateNews(displayNews.id, { comments: updatedComments }, firestoreUser);
      
      message.success(t('common:deleteSuccess'));
      
      // Always invalidate and refetch to get the latest data
      queryClient.invalidateQueries({ queryKey: ['news', id] });
      queryClient.invalidateQueries({ queryKey: ['news'] });
      await refetch();
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      message.error(error.message || t('common:deleteError'));
    }
  };

  // Loading state - only show if no state data is available
  if (isLoading && !state?.news) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayNews) {
    return <Card>News not found</Card>;
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              {t('common:back')}
            </Button>
            <Title level={3}>{displayNews.title}</Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>
      
      {/* Tabs */}
      <Card>
        <Tabs
          defaultActiveKey="details"
          items={[
            {
              key: 'details',
              label: t('news:details'),
              children: (
                <div>
                  <Descriptions bordered column={1} style={{ marginBottom: 24 }}>
                    <Descriptions.Item label={t('news:table.title')}>
                      <Text strong>{displayNews.title}</Text>
                    </Descriptions.Item>
                    
                    <Descriptions.Item label={t('news:table.description')}>
                      <Paragraph>{displayNews.description}</Paragraph>
                    </Descriptions.Item>
                    
                    <Descriptions.Item label={t('news:table.author')}>
                      {displayNews.author.name}
                    </Descriptions.Item>
                    
                    <Descriptions.Item label={t('news:table.category')}>
                      <Tag color={newsCategoryColors[displayNews.category]}>
                        {t(`news:categories.${displayNews.category}`)}
                      </Tag>
                    </Descriptions.Item>
                    
                    <Descriptions.Item label={t('news:table.status')}>
                      <Tag color={newsStatusColors[displayNews.status]}>
                        {t(`news:statuses.${displayNews.status}`)}
                      </Tag>
                    </Descriptions.Item>
                    
                    <Descriptions.Item label={t('news:table.publishDate')}>
                      {dayjs(displayNews.publishDate).format('YYYY-MM-DD HH:mm')}
                    </Descriptions.Item>
                    
                    {displayNews.expiryDate && (
                      <Descriptions.Item label={t('news:table.expiryDate')}>
                        {dayjs(displayNews.expiryDate).format('YYYY-MM-DD HH:mm')}
                      </Descriptions.Item>
                    )}
                    
                    {displayNews.attachments && displayNews.attachments.length > 0 && (
                      <Descriptions.Item label={t('news:table.attachments')}>
                        {displayNews.attachments.length} {t('common:items')}
                      </Descriptions.Item>
                    )}
                    
                    {displayNews.photos && displayNews.photos.length > 0 && (
                      <Descriptions.Item label={t('news:table.photos')}>
                        <div style={{ marginTop: 8 }}>
                          <Row gutter={[8, 8]}>
                            {displayNews.photos.map((photo, index) => (
                              <Col key={photo.id} xs={24} sm={12} md={8} lg={6}>
                                <img
                                  src={photo.url}
                                  alt={`Photo ${index + 1}`}
                                  style={{
                                    width: '100%',
                                    height: 120,
                                    objectFit: 'cover',
                                    borderRadius: 8,
                                    border: '1px solid #d9d9d9'
                                  }}
                                />
                              </Col>
                            ))}
                          </Row>
                        </div>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                  
                  {/* Like and Comment Actions */}
                  <div style={{ marginBottom: 24 }}>
                    <Space size="large">
                      <Button 
                        type="text" 
                        icon={<HeartOutlined />} 
                        onClick={handleLike}
                        style={{ color: '#e91e63' }}
                      >
                        {displayNews.liked?.count || 0} {t('common:likes')}
                      </Button>
                      <span style={{ color: '#666' }}>
                        💬 {displayNews.comments?.length || 0} {t('common:comments')}
                      </span>
                    </Space>
                  </div>
                  
                  {/* Comments Section */}
                  <div>
                    <Typography.Title level={4}>{t('common:comments')}</Typography.Title>
                    <CommentsFormSection
                      comments={displayNews.comments || []}
                      onAddComment={handleAddComment}
                      onUpdateComment={handleUpdateComment}
                      onDeleteComment={handleDeleteComment}
                      showForm={true}
                      maxHeight="400px"
                    />
                  </div>
                </div>
              ),
            },
            // Only show reference tab if reference exists
            ...(displayNews.reference ? [{
              key: 'reference',
              label: t('news:reference'),
              children: (() => {
                switch (displayNews.reference!.type) {
                  case 'alert':
                    return <AlertTab alertId={displayNews.reference!.id} />;
                  case 'event':
                    return <EventTab eventId={displayNews.reference!.id} />;
                  case 'guideline':
                    return <GuidelineTab guidelineId={displayNews.reference!.id} />;
                  case 'incident':
                    return <IncidentTab incidentId={displayNews.reference!.id} />;
                  case 'lostAndFound':
                    return <LostAndFoundTab itemId={displayNews.reference!.id} />;
                  default:
                    return <div>Unknown reference type</div>;
                }
              })(),
            }] : []),
          ]}
        />
      </Card>

      {/* Edit Modal */}
      <NewsModal
        open={isEditModalOpen}
        news={displayNews}
        onSuccess={handleEditSuccess}
        onCancel={handleEditCancel}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('news:confirmDeleteTitle')}
        error={deleteError}
        onErrorDismiss={handleErrorDismiss}
      />
    </div>
  );
};

export default NewsDetailsPage; 